import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { fetchCourseById, sendCourseNotification, uploadCourseFile, deleteCourseFile } from "../pages/services/courseService";
import { fetchWhatsAppTemplates } from "../pages/services/studentService";
import { fetchAssignmentsByBatchId } from "../pages/services/assignmentService";
import NotificationHistorySection from "./NotificationHistorySection";
import { WEBSITE_COURSE_URL } from "./utils/courseSlug";
import "../styles/BatchDetails.css";
import "../styles/StudentDetails.css";

const BUDDY_BASE = "https://buddy.myskillverse.ai/assignment.html";

const PARAM_OPTIONS = [
  { value: "student_name", label: "Student Name" },
  { value: "parent_name", label: "Parent Name" },
  { value: "batch_name", label: "Batch Name" },
  { value: "assignment_link", label: "Assignment Link" },
  { value: "custom", label: "Custom" },
];

const buildEmailBodyPreview = (body, paramSelections) => {
  if (!body) return "";
  let text = body;
  Object.entries(paramSelections).forEach(([num, sel]) => {
    let val = `{${num}}`;
    if (sel.type === "student_name") val = "[Student Name]";
    else if (sel.type === "parent_name") val = "[Parent Name]";
    else if (sel.type === "batch_name") val = "[Batch Name]";
    else if (sel.type === "assignment_link") val = sel.value || "[Assignment Link]";
    else if (sel.type === "custom") val = sel.value || "[Custom]";
    text = text.replaceAll(`{${num}}`, val);
  });
  return text;
};

const buildWaPreview = (body, paramNames, paramSelections) => {
  if (!body) return "";
  let msg = body;
  paramNames.forEach((_, idx) => {
    const sel = paramSelections[idx];
    let val = `{{${idx + 1}}}`;
    if (sel?.type === "student_name") val = "[Student Name]";
    else if (sel?.type === "parent_name") val = "[Parent Name]";
    else if (sel?.type === "batch_name") val = "[Batch Name]";
    else if (sel?.type === "assignment_link") val = sel.value || "[Assignment Link]";
    else if (sel?.type === "custom") val = sel.value || "[Custom]";
    msg = msg.replace(`{{${idx + 1}}}`, val);
  });
  return msg;
};

/* ── Course Notification Modal ─────────────────────────────── */
const CourseNotificationModal = ({ isOpen, onClose, course, studentCount, assignments }) => {
  const [notifType, setNotifType] = useState("email");
  const [emailTitle, setEmailTitle] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailParamSelections, setEmailParamSelections] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState(null);
  const [sentTypes, setSentTypes] = useState(new Set());
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [paramSelections, setParamSelections] = useState([]);
  const fileInputRef = useRef(null);

  const emailParamNums = emailBody
    ? [...new Set([...emailBody.matchAll(/\{(\d+)\}/g)].map((m) => m[1]))].sort((a, b) => Number(a) - Number(b))
    : [];

  useEffect(() => {
    if (notifType === "whatsapp" && templates.length === 0) {
      setTemplatesLoading(true);
      fetchWhatsAppTemplates().then(setTemplates).catch(() => setTemplates([])).finally(() => setTemplatesLoading(false));
    }
  }, [notifType]);

  const paramNames = selectedTemplate?.params
    ? selectedTemplate.params.split(",").map((p) => p.trim()).filter(Boolean)
    : [];

  const resetForm = () => {
    setNotifType("email"); setEmailTitle(""); setEmailBody(""); setEmailParamSelections({});
    setAttachments([]); setShowPreview(false); setResult(null); setSentTypes(new Set()); setSelectedTemplate(null); setParamSelections([]);
  };

  const handleClose = () => { resetForm(); onClose(); };

  const handleTemplateChange = (templateId) => {
    const tmpl = templates.find((t) => t._id === templateId) || null;
    setSelectedTemplate(tmpl);
    if (tmpl) {
      const names = tmpl.params ? tmpl.params.split(",").map((p) => p.trim()).filter(Boolean) : [];
      setParamSelections(names.map(() => ({ type: "", value: "", assignmentId: "" })));
    } else {
      setParamSelections([]);
    }
  };

  const handleEmailParamAssignmentChange = (num, assignmentId) => {
    const asgn = (assignments || []).find((a) => a._id === assignmentId);
    setEmailParamSelections((p) => ({
      ...p,
      [num]: { type: "assignment_link", assignmentId, value: asgn ? `${BUDDY_BASE}?id=${asgn._id}` : "" },
    }));
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith("image/")) return "🖼️";
    if (file.type.startsWith("video/")) return "🎬";
    if (file.type === "application/pdf") return "📄";
    return "📎";
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canSendWhatsApp = selectedTemplate &&
    paramNames.every((_, idx) => {
      const sel = paramSelections[idx];
      if (!sel?.type) return false;
      if (sel.type === "custom" && !sel.value?.trim()) return false;
      if (sel.type === "assignment_link" && !sel.value?.trim()) return false;
      return true;
    });

  const canSend = notifType === "email" ? (emailTitle.trim() || emailBody.trim()) : canSendWhatsApp;

  const handleSend = async () => {
    setIsSending(true);
    setResult(null);
    try {
      const res = await sendCourseNotification(
        course._id,
        notifType,
        emailTitle,
        emailBody,
        attachments,
        notifType === "whatsapp" ? { templateName: selectedTemplate.templateName, paramSelections } : null,
        notifType === "email" && emailParamNums.length > 0 ? { paramSelections: emailParamSelections } : null
      );
      setResult({ success: true, sent: res.sent, failed: res.failed, details: res.details });
      setSentTypes(prev => new Set([...prev, notifType]));
    } catch (err) {
      setResult({ success: false, error: err.message });
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="notif-overlay" onClick={handleClose}>
      <div className="notif-modal" onClick={(e) => e.stopPropagation()}>
        <div className="notif-modal-header">
          <h2>Send Course Notification</h2>
          <button className="notif-close-btn" onClick={handleClose}>×</button>
        </div>
        <div className="notif-modal-body">
          <div className="notif-radio-group">
            <label className={`notif-radio-option ${notifType === "email" ? "notif-radio-selected" : ""}`}>
              <input type="radio" name="courseNotifType" value="email" checked={notifType === "email"} onChange={() => { setNotifType("email"); setShowPreview(false); }} />
              <span className="notif-radio-icon">✉️</span> Email
            </label>
            <label className={`notif-radio-option ${notifType === "whatsapp" ? "notif-radio-selected" : ""}`}>
              <input type="radio" name="courseNotifType" value="whatsapp" checked={notifType === "whatsapp"} onChange={() => { setNotifType("whatsapp"); setShowPreview(false); }} />
              <span className="notif-radio-icon">💬</span> WhatsApp
            </label>
          </div>

          <div className="bd-notif-recipients">
            <span className="bd-notif-recipients-icon">👥</span>
            <span>
              Sending to <strong>{studentCount}</strong> student{studentCount !== 1 ? "s" : ""} enrolled in{" "}
              <strong>{course?.courseTitle}</strong>
            </span>
          </div>

          {notifType === "email" && (
            <div className="notif-email-fields">
              <div className="notif-field">
                <label className="notif-label">Subject</label>
                <input type="text" value={emailTitle} onChange={(e) => setEmailTitle(e.target.value)} placeholder="Enter email subject…" className="notif-input" />
              </div>
              <div className="notif-field">
                <label className="notif-label">
                  Body
                  <span className="notif-param-hint">Use {"{1}"}, {"{2}"}… for dynamic values</span>
                </label>
                <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)}
                  placeholder={"Write your email content here…\nTip: use {1}, {2} for student name, custom text, etc."}
                  className="notif-textarea" rows={5} />
              </div>

              {emailParamNums.length > 0 && (
                <div className="notif-field">
                  <label className="notif-label">Placeholder Values</label>
                  <div className="wa-params-list">
                    {emailParamNums.map((num) => (
                      <div key={num} className="wa-param-row">
                        <span className="wa-param-name">{`{${num}}`}</span>
                        <div className="wa-param-select-wrap">
                          <select className="notif-input wa-select" value={emailParamSelections[num]?.type || ""}
                            onChange={(e) => setEmailParamSelections((p) => ({ ...p, [num]: { type: e.target.value, value: "", assignmentId: "" } }))}>
                            <option value="">Select value…</option>
                            {PARAM_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                          </select>
                          {emailParamSelections[num]?.type === "custom" && (
                            <textarea className="notif-textarea wa-custom-textarea" rows={2} placeholder="Enter custom value…"
                              value={emailParamSelections[num]?.value || ""}
                              onChange={(e) => setEmailParamSelections((p) => ({ ...p, [num]: { ...p[num], value: e.target.value } }))} />
                          )}
                          {emailParamSelections[num]?.type === "assignment_link" && (
                            <div className="wa-assignment-picker">
                              <select
                                className="notif-input wa-select"
                                value={emailParamSelections[num]?.assignmentId || ""}
                                onChange={(e) => handleEmailParamAssignmentChange(num, e.target.value)}
                              >
                                <option value="">Select assignment…</option>
                                {(assignments || []).map((a) => {
                                  const parts = (a.assignmentTime || "").split(" - ");
                                  const fmt = (s) => { const d = new Date(s?.trim()); return isNaN(d) ? s : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
                                  const window = parts.length === 2 ? ` | ${fmt(parts[0])} → ${fmt(parts[1])}` : "";
                                  return <option key={a._id} value={a._id}>{a.assignmentTitle}{window}</option>;
                                })}
                              </select>
                              {emailParamSelections[num]?.value && (
                                <span className="wa-assignment-link-preview">{emailParamSelections[num].value}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="notif-field">
                <label className="notif-label">Attachments</label>
                <input ref={fileInputRef} type="file" multiple onChange={(e) => { setAttachments((p) => [...p, ...Array.from(e.target.files)]); e.target.value = ""; }} className="notif-file-input-hidden" />
                <button type="button" className="notif-file-btn" onClick={() => fileInputRef.current?.click()}>+ Choose Files</button>
                <span className="notif-file-hint">PDF, Images, Videos and more supported</span>
                {attachments.length > 0 && (
                  <div className="notif-attachments-list">
                    {attachments.map((file, idx) => (
                      <div key={idx} className="notif-attachment-chip">
                        <span className="notif-attachment-icon">{getFileIcon(file)}</span>
                        <span className="notif-attachment-name">{file.name}</span>
                        <span className="notif-attachment-size">({formatFileSize(file.size)})</span>
                        <button className="notif-attachment-remove" onClick={() => setAttachments((p) => p.filter((_, i) => i !== idx))}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button type="button" className="notif-preview-toggle" onClick={() => setShowPreview((p) => !p)}>
                {showPreview ? "▲ Hide Preview" : "▼ Show Preview"}
              </button>

              {showPreview && (
                <div className="notif-preview-wrapper">
                  <div className="notif-preview-bar"><span>Email Preview</span><span className="notif-preview-hint">As seen by each recipient</span></div>
                  <div className="notif-email-preview">
                    <div className="preview-email-chrome"><div className="preview-chrome-dots"><span /><span /><span /></div><span className="preview-chrome-title">Mail</span></div>
                    <div className="preview-email-body-wrapper">
                      <div className="preview-meta-row"><span className="preview-field-label">From</span><span className="preview-field-value">SkillVerse &lt;noreply@skillverse.in&gt;</span></div>
                      <div className="preview-meta-row"><span className="preview-field-label">To</span><span className="preview-field-value">All students in {course?.courseTitle} ({studentCount} recipient{studentCount !== 1 ? "s" : ""})</span></div>
                      <div className="preview-meta-row preview-subject-row"><span className="preview-subject-text">{emailTitle || <em className="preview-empty">No subject</em>}</span></div>
                      <div className="preview-email-content">
                        {emailBody ? <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{buildEmailBodyPreview(emailBody, emailParamSelections)}</p> : <p className="preview-empty">Email body will appear here…</p>}
                      </div>
                      {attachments.length > 0 && (
                        <div className="preview-attachments-row">
                          <span className="preview-attachments-label">📎 Attachments</span>
                          <div className="preview-attachment-chips">
                            {attachments.map((file, idx) => (
                              <span key={idx} className="preview-attachment-pill">
                                {getFileIcon(file)} {file.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {notifType === "whatsapp" && (
            <div className="notif-email-fields">
              <div className="notif-field">
                <label className="notif-label">Template</label>
                {templatesLoading ? (
                  <span className="notif-file-hint">Loading templates...</span>
                ) : (
                  <select className="notif-input wa-select" value={selectedTemplate?._id || ""} onChange={(e) => handleTemplateChange(e.target.value)}>
                    <option value="">Select a template...</option>
                    {templates.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
                  </select>
                )}
              </div>

              {selectedTemplate && paramNames.length > 0 && (
                <div className="notif-field">
                  <label className="notif-label">Parameters</label>
                  <div className="wa-params-list">
                    {paramNames.map((paramName, idx) => (
                      <div key={idx} className="wa-param-row">
                        <span className="wa-param-name">{paramName}</span>
                        <div className="wa-param-select-wrap">
                          <select className="notif-input wa-select" value={paramSelections[idx]?.type || ""}
                            onChange={(e) => setParamSelections((prev) => { const n = [...prev]; n[idx] = { type: e.target.value, value: "", assignmentId: "" }; return n; })}>
                            <option value="">Select value...</option>
                            {PARAM_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                          </select>
                          {paramSelections[idx]?.type === "custom" && (
                            <textarea className="notif-textarea wa-custom-textarea" rows={2} placeholder="Enter custom value…"
                              value={paramSelections[idx]?.value || ""}
                              onChange={(e) => setParamSelections((prev) => { const n = [...prev]; n[idx] = { ...n[idx], value: e.target.value }; return n; })} />
                          )}
                          {paramSelections[idx]?.type === "assignment_link" && (
                            <div className="wa-assignment-picker">
                              <select
                                className="notif-input wa-select"
                                value={paramSelections[idx]?.assignmentId || ""}
                                onChange={(e) => {
                                  const asgn = (assignments || []).find((a) => a._id === e.target.value);
                                  setParamSelections((prev) => {
                                    const n = [...prev];
                                    n[idx] = { type: "assignment_link", assignmentId: e.target.value, value: asgn ? `${BUDDY_BASE}?id=${asgn._id}` : "" };
                                    return n;
                                  });
                                }}
                              >
                                <option value="">Select assignment…</option>
                                {(assignments || []).map((a) => {
                                  const parts = (a.assignmentTime || "").split(" - ");
                                  const fmt = (s) => { const d = new Date(s?.trim()); return isNaN(d) ? s : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
                                  const window = parts.length === 2 ? ` | ${fmt(parts[0])} → ${fmt(parts[1])}` : "";
                                  return <option key={a._id} value={a._id}>{a.assignmentTitle}{window}</option>;
                                })}
                              </select>
                              {paramSelections[idx]?.value && (
                                <span className="wa-assignment-link-preview">{paramSelections[idx].value}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedTemplate && (
                <div className="wa-preview-wrapper">
                  <div className="wa-preview-bar"><span>WhatsApp Preview</span><span className="notif-preview-hint">Updates as you fill params</span></div>
                  <div className="wa-preview-screen">
                    <div className="wa-preview-bubble">
                      <p className="wa-preview-text" style={{ whiteSpace: "pre-wrap" }}>
                        {buildWaPreview(selectedTemplate?.body || "", paramNames, paramSelections) || <em style={{ color: "#9ca3af" }}>Fill in params to see preview...</em>}
                      </p>
                      <span className="wa-preview-meta">10:30 AM ✓✓</span>
                    </div>
                  </div>
                </div>
              )}
              <span className="notif-file-hint">Will be sent to all {studentCount} enrolled student contact numbers</span>
            </div>
          )}

          {result?.success && (
            <div className="bd-notif-result">
              <div className="bd-notif-result-counts">
                <span className="bd-notif-result-sent">✓ Sent: {result.sent}</span>
                {result.failed > 0 && <span className="bd-notif-result-failed">✕ Failed: {result.failed}</span>}
              </div>
              {result.failed > 0 && result.details?.failed?.length > 0 && (
                <div className="bd-notif-failed-list">
                  <p className="bd-notif-failed-label">Could not reach:</p>
                  {result.details.failed.map((f, i) => (
                    <div key={i} className="bd-notif-failed-row">
                      <span className="bd-notif-failed-name">{f.name}</span>
                      <span className="bd-notif-failed-reason">{f.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {result && !result.success && <div className="notif-status notif-status-error">{result.error}</div>}
        </div>

        <div className="notif-modal-footer">
          <button className="notif-cancel-btn" onClick={handleClose} disabled={isSending}>Cancel</button>
          <button className="notif-send-btn" onClick={handleSend} disabled={isSending || !canSend || sentTypes.has(notifType)}>
            {isSending ? "Sending…" : `Send ${notifType === "email" ? "Email" : "WhatsApp"} to All`}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Brochures: the files lead management sends ──────────────── */
// The course brochure and fee structure. Lead messages use their links (the
// "Course — brochure link" / "fee structure link" template parameters), and a
// WhatsApp document template can attach them — always the version here.
const COURSE_FILES = [
  { kind: "brochure", field: "courseBrochure", label: "Course brochure" },
  { kind: "feeStructure", field: "feeStructureBrochure", label: "Fee structure" },
];

// The friendly link (api.myskillverse.ai/course/<slug>-brochure.pdf) stays the
// same when the file is replaced — it always opens the current one.
const CopyLink = ({ link }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard?.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  return <button type="button" className="bd-btn" style={{ fontSize: 12, padding: "4px 10px" }} onClick={copy}>{copied ? "Copied" : "Copy link"}</button>;
};

const CourseFilesCard = ({ course, onChange }) => {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const inputs = useRef({});

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    setError("");
    try {
      onChange(await uploadCourseFile(course._id, kind, file));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
      if (inputs.current[kind]) inputs.current[kind].value = "";
    }
  };
  const remove = async (kind, label) => {
    if (!window.confirm(`Remove the ${label.toLowerCase()}? Lead messages that use it will be skipped until a new one is uploaded.`)) return;
    setBusy(kind);
    setError("");
    try {
      onChange(await deleteCourseFile(course._id, kind));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bd-card">
      <p className="bd-card-heading">Brochures</p>
      <p style={{ margin: "0 0 10px", fontSize: 12, color: "#6b7280" }}>Sent to leads by email and WhatsApp (Lead settings → Course automation). PDF, image, Word or PowerPoint, up to 15 MB. A file's link stays the same when you replace it.</p>
      {COURSE_FILES.map(({ kind, field, label }) => {
        const file = course[field] || {};
        const link = course.fileLinks?.[kind] || file.url;
        return (
          <div key={kind} style={{ padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>{label}</div>
            {file.url ? (
              <>
                <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: "#3b82f6", fontSize: 13, wordBreak: "break-all" }}>{link}</a>
                <div style={{ fontSize: 11.5, color: "#6b7280" }}>📎 {file.fileName}</div>
              </>
            ) : (
              <span style={{ fontSize: 13, color: "#b45309" }}>Not uploaded</span>
            )}
            {file.uploadedAt && <div style={{ fontSize: 11.5, color: "#9ca3af" }}>Uploaded {new Date(file.uploadedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <label className="bd-btn" style={{ cursor: busy ? "default" : "pointer", fontSize: 12, padding: "4px 10px", opacity: busy && busy !== kind ? 0.6 : 1 }}>
                {busy === kind ? "Working…" : file.url ? "Replace" : "Upload"}
                <input ref={(el) => { inputs.current[kind] = el; }} type="file" hidden disabled={!!busy}
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.pptx" onChange={(e) => upload(kind, e.target.files[0])} />
              </label>
              {file.url && <CopyLink link={link} />}
              {file.url && <button type="button" className="bd-btn" style={{ fontSize: 12, padding: "4px 10px", color: "#b91c1c" }} disabled={!!busy} onClick={() => remove(kind, label)}>Remove</button>}
            </div>
          </div>
        );
      })}
      {error && <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "#b91c1c" }}>{error}</p>}
    </div>
  );
};

/* ── Course Details Page ────────────────────────────────────── */
const CourseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    fetchCourseById(id)
      .then(setCourse)
      .catch(() => setError("Error fetching course details"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!course?.linkedBatches?.length) return;
    Promise.all(course.linkedBatches.map((b) => fetchAssignmentsByBatchId(b._id)))
      .then((results) => setAssignments(results.flat()))
      .catch(() => setAssignments([]));
  }, [course]);

  if (loading) {
    return (
      <div className="bd-overlay">
        <div className="bd-loader" />
        <p className="bd-loading-text">Loading...</p>
      </div>
    );
  }

  if (error || !course) {
    return <p className="bd-error-text">{error || "No course data found"}</p>;
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "N/A";
  const formatFee = (fee) => fee != null ? `₹${Number(fee).toLocaleString("en-IN")}` : "—";

  const InfoRow = ({ label, value }) => (
    <div className="bd-info-row">
      <span className="bd-info-label">{label}</span>
      <span className="bd-info-value">{value ?? "—"}</span>
    </div>
  );

  return (
    <div className="bd-container">
      <CourseNotificationModal
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        course={course}
        studentCount={course.studentsEnrolled || 0}
        assignments={assignments}
      />

      {/* Header */}
      <div className="bd-header">
        <div className="bd-header-title">
          <div className="bd-avatar" style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }}>
            {course.courseTitle?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <h1 className="bd-title">{course.courseTitle}</h1>
            <span style={{ fontSize: 13, color: "#6b7280" }}>{course.courseDuration || "—"}</span>
          </div>
        </div>
        <div className="bd-header-actions">
          <button className="notif-trigger-btn" onClick={() => setShowNotificationModal(true)}>Send Notification</button>
          <button className="bd-btn" style={{ background: "#ede9fe", color: "#5b21b6", border: "1px solid #c4b5fd" }} onClick={() => navigate(`/courses/${course._id}/notes`)}>Manage Notes</button>
          <button className="bd-btn bd-btn-primary" onClick={() => navigate(`/courses/${course._id}/update`)}>Edit Course</button>
        </div>
      </div>

      <div className="bd-body">
        {/* Sidebar */}
        <div className="bd-sidebar">
          <div className="bd-card">
            <p className="bd-card-heading">Key Info</p>
            {/* The course's page on the public website. A course without a slug
                still lists on the landing page but its card goes nowhere, so the
                gap is called out rather than shown as a quiet dash. */}
            <InfoRow label="Website Page" value={
              course.courseSlug ? (
                <a href={`${WEBSITE_COURSE_URL}${course.courseSlug}`} target="_blank" rel="noopener noreferrer"
                  style={{ color: "#3b82f6", wordBreak: "break-all" }}>
                  /courses/{course.courseSlug}
                </a>
              ) : (
                <span style={{ color: "#b45309", fontWeight: 600 }}>No slug — page can't open. Edit the course to add one.</span>
              )
            } />
            <InfoRow label="Duration" value={course.courseDuration} />
            <InfoRow label="Fee" value={formatFee(course.courseFee)} />
            <InfoRow label="Created" value={formatDate(course.createdDateTime)} />
            <InfoRow label="Updated" value={formatDate(course.updatedDateTime)} />
          </div>

          <CourseFilesCard course={course} onChange={(files) => setCourse((c) => ({ ...c, ...files }))} />

          <div className="bd-card">
            <p className="bd-card-heading">Stats</p>
            <InfoRow label="Current Batches" value={
              <span style={{ background: "#dcfce7", color: "#16a34a", padding: "2px 10px", borderRadius: 10, fontSize: 12, fontWeight: 700 }}>{course.currentBatches}</span>
            } />
            <InfoRow label="Completed Batches" value={
              <span style={{ background: "#dbeafe", color: "#1d4ed8", padding: "2px 10px", borderRadius: 10, fontSize: 12, fontWeight: 700 }}>{course.completedBatches}</span>
            } />
            <InfoRow label="Students Enrolled" value={
              <span style={{ background: "#ede9fe", color: "#5b21b6", padding: "2px 10px", borderRadius: 10, fontSize: 12, fontWeight: 700 }}>{course.studentsEnrolled}</span>
            } />
          </div>

          {course.linkedBatches?.length > 0 && (
            <div className="bd-card">
              <p className="bd-card-heading">Linked Batches ({course.linkedBatches.length})</p>
              {course.linkedBatches.map((b) => (
                <div key={b._id} className="bd-info-row" style={{ cursor: "pointer" }} onClick={() => navigate(`/batches/${b._id}`)}>
                  <span className="bd-info-label" style={{ color: "#3b82f6", fontWeight: 600 }}>{b.batchTitle}</span>
                  <span className={`bd-note-status bd-note-${b.batchStatus?.toLowerCase()}`}>{b.batchStatus}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main */}
        <div className="bd-main">
          {/* Description */}
          <div className="bd-card">
            <p className="bd-card-heading">About this Course</p>
            <p className="bd-description">{course.courseDescription || "No description provided."}</p>
          </div>

          {/* Course Image */}
          {course.courseImg && (
            <div className="bd-card">
              <p className="bd-card-heading">Course Image</p>
              <img src={"https://uraansoftskills.com/SkillVerse/images/"+course.courseImg} alt={course.courseTitle} style={{ maxWidth: "100%", borderRadius: 8, maxHeight: 240, objectFit: "cover" }} />
            </div>
          )}

          {/* Features */}
          {course.courseFeatures?.length > 0 && (
            <div className="bd-card">
              <p className="bd-card-heading">Course Features ({course.courseFeatures.length})</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, backgroundColor: "bisque"}}>
                {course.courseFeatures.map((f, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                    {f.featureIcon && <img src={"https://uraansoftskills.com/SkillVerse/icons/"+f.featureIcon} alt="Feature Icon"/>}
                    {/* <span style={{ fontSize: 22, flexShrink: 0 }}>{f.featureIcon}</span> */}
                    <div>
                      <p style={{ margin: "0 0 3px", fontWeight: 600, color: "#111827", fontSize: 14 }}>{f.featureTitle}</p>
                      <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>{f.featureDescription}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tools */}
          {course.Tools?.length > 0 && (
            <div className="bd-card">
              <p className="bd-card-heading">Tools & Technologies ({course.Tools.length})</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {course.Tools.map((t, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 12px" }}>
                    {t.toolImg && <img src={"https://uraansoftskills.com/SkillVerse/icons/"+t.toolImg} alt={t.toolsName} style={{ width: 24, height: 24, objectFit: "contain" }} />}
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{t.toolsName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Curriculum */}
          {(course.courseCurriculum?.length > 0 || course.curriculumDescription) && (
            <div className="bd-card">
              <p className="bd-card-heading">Curriculum</p>
              {course.curriculumDescription && (
                <p className="bd-description" style={{ marginBottom: 16 }}>{course.curriculumDescription}</p>
              )}
              {course.courseCurriculum?.map((mod, i) => (
                <CurriculumModule key={i} module={mod} index={i} />
              ))}
            </div>
          )}

          {/* FAQ */}
          {course.faq?.length > 0 && (
            <div className="bd-card">
              <p className="bd-card-heading">FAQ ({course.faq.length})</p>
              {course.faq.map((item, i) => (
                <FaqItem key={i} item={item} />
              ))}
            </div>
          )}

          <NotificationHistorySection entityType="course" entityId={id} />
        </div>
      </div>
    </div>
  );
};

const CurriculumModule = ({ module, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div style={{ marginBottom: 10, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
      <div
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f9fafb", cursor: "pointer" }}
        onClick={() => setOpen((p) => !p)}
      >
        <div>
          <span style={{ fontWeight: 600, color: "#111827", fontSize: 14 }}>Module {index + 1}: {module.moduleTitle}</span>
          {module.moduleDuration && <span style={{ marginLeft: 10, fontSize: 12, color: "#6b7280" }}>· {module.moduleDuration}</span>}
        </div>
        <span style={{ color: "#6b7280", fontSize: 13 }}>{open ? "▲" : "▼"} {module.topics?.length || 0} topics</span>
      </div>
      {open && module.topics?.length > 0 && (
        <div style={{ padding: "8px 14px" }}>
          {module.topics.map((t, j) => (
            <div key={j} style={{ padding: "7px 0", borderBottom: "1px solid #f3f4f6" }}>
              <p style={{ margin: "0 0 2px", fontWeight: 600, fontSize: 13, color: "#374151" }}>{t.title}</p>
              {t.description && <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>{t.description}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FaqItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 8, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
      <div
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", cursor: "pointer", background: open ? "#fafafa" : "#fff" }}
        onClick={() => setOpen((p) => !p)}
      >
        <span style={{ fontWeight: 600, color: "#111827", fontSize: 14 }}>{item.faqQuestion}</span>
        <span style={{ color: "#6b7280", fontSize: 13, flexShrink: 0, marginLeft: 12 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ padding: "10px 14px", borderTop: "1px solid #f3f4f6", background: "#fafafa" }}>
          <p style={{ margin: 0, fontSize: 13.5, color: "#374151", lineHeight: 1.6 }}>{item.faqAnswer}</p>
        </div>
      )}
    </div>
  );
};

export default CourseDetails;
